import * as THREE from 'three';
import type { Box } from '../world/Level';

/** A hollow cloth prop, hung from the latch-side top corner of the sauna door. */
export function buildSaunaBra(door: Box): THREE.Group {
  const prop = new THREE.Group();
  prop.name = 'saunan-rintsikat';
  // Local X points toward the handle (+Z in the hall), Z toward the player.
  prop.rotation.y = -Math.PI / 2;
  prop.position.set(door.minX - 0.052, door.maxY, door.maxZ - 0.015);

  const cloth = new THREE.MeshLambertMaterial({
    color: 0xd51e35, side: THREE.DoubleSide, flatShading: true,
  });
  const edging = new THREE.MeshLambertMaterial({ color: 0x8c1026, side: THREE.DoubleSide });

  function ribbon(parent: THREE.Group, points: number[][], width: number): void {
    const curve = new THREE.CatmullRomCurve3(points.map(([x, y, z]) => new THREE.Vector3(x, y, z)));
    const vertices: number[] = [];
    const indices: number[] = [];
    const segments = 24;
    for (let i = 0; i <= segments; i++) {
      const p = curve.getPoint(i / segments);
      const tangent = curve.getTangent(i / segments);
      const across = new THREE.Vector3(-tangent.y, tangent.x, 0).normalize().multiplyScalar(width / 2);
      vertices.push(...p.clone().add(across).toArray(), ...p.clone().sub(across).toArray());
      if (i < segments) {
        const a = i * 2;
        indices.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
      }
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    parent.add(new THREE.Mesh(geometry, cloth));
  }

  const garment = new THREE.Group();
  garment.position.set(-0.17, -0.35, 0.025);
  garment.rotation.z = 0.22;
  prop.add(garment);

  const outline = new THREE.Shape();
  outline.moveTo(0.095, 0.015);
  outline.bezierCurveTo(0.04, 0.03, -0.03, 0.085, -0.07, 0.13);
  outline.bezierCurveTo(-0.13, 0.055, -0.12, -0.04, -0.08, -0.075);
  outline.bezierCurveTo(-0.02, -0.13, 0.085, -0.075, 0.095, 0.015);
  const boundary = outline.getPoints(5).slice(0, -1);

  // Open, shallow cups with a shaped upper edge, rather than solid spheres.
  for (const side of [-1, 1]) {
    const mirror = -side;
    const vertices = [side * 0.102, -0.008, 0.048];
    const indices: number[] = [];
    const rings = 4;
    const n = boundary.length;
    for (let ring = 1; ring <= rings; ring++) {
      const r = ring / rings;
      for (const p of boundary) {
        vertices.push(
          side * 0.102 + mirror * p.x * r,
          -0.008 + (p.y + 0.008) * r,
          0.048 * Math.sqrt(1 - r * r),
        );
      }
      for (let i = 0; i < n; i++) {
        const a = 1 + (ring - 1) * n + i;
        const b = 1 + (ring - 1) * n + (i + 1) % n;
        if (ring === 1) indices.push(0, a, b);
        else indices.push(a - n, a, b, a - n, b, b - n);
      }
    }
    if (side === 1) {
      for (let i = 0; i < indices.length; i += 3) {
        [indices[i + 1], indices[i + 2]] = [indices[i + 2], indices[i + 1]];
      }
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    const cup = new THREE.Mesh(geometry, cloth);
    cup.name = side < 0 ? 'kuppi-vasen' : 'kuppi-oikea';
    garment.add(cup);

    const seam = boundary.map((p) => new THREE.Vector3(side * 0.102 + mirror * p.x, p.y, 0.002));
    garment.add(new THREE.Mesh(
      new THREE.TubeGeometry(new THREE.CatmullRomCurve3(seam, true), 36, 0.003, 4, true), edging,
    ));
  }

  ribbon(garment, [[-0.21, -0.035, 0], [-0.11, -0.08, -0.008], [0, -0.045, 0], [0.11, -0.08, -0.008], [0.21, -0.035, 0]], 0.025);
  ribbon(garment, [[-0.018, 0.014, 0.004], [0, 0.006, 0.013], [0.018, 0.014, 0.004]], 0.018);

  // The right strap folds over the frame at the opening edge; the other
  // hangs loose, making the point of support visible from the hall.
  ribbon(prop, [[-0.033, -0.185, 0.028], [-0.018, -0.08, 0.015], [0, 0.089, 0.008], [0, 0.096, -0.022], [0, 0.084, -0.053]], 0.017);
  ribbon(garment, [[-0.172, 0.13, 0], [-0.225, 0.105, 0.008], [-0.26, -0.025, 0.018], [-0.24, -0.14, 0.025], [-0.21, -0.1, 0.008], [-0.21, -0.035, 0]], 0.014);

  const bow = new THREE.Shape();
  bow.moveTo(0, 0);
  bow.lineTo(-0.022, 0.012);
  bow.lineTo(-0.022, -0.01);
  bow.lineTo(0, 0);
  bow.lineTo(0.022, 0.012);
  bow.lineTo(0.022, -0.01);
  bow.closePath();
  const bowMesh = new THREE.Mesh(new THREE.ShapeGeometry(bow), edging);
  bowMesh.position.set(0, 0.008, 0.026);
  garment.add(bowMesh);

  prop.traverse((object) => {
    if (object instanceof THREE.Mesh) {
      object.castShadow = true;
      object.receiveShadow = true;
    }
  });
  return prop;
}
